import {
  AfterContentInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Inject,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { AppService } from '@nk/access-block';
import { AuthInfo, AuthStatus, GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import { EmailValidators } from '@nk/ui-lib';
import { arrayToDict } from '@nk/utils-lib';
import { Auth0Error } from 'auth0-js';

@Component({
  selector: 'nk-login-unauthenticated',
  templateUrl: 'login-unauthenticated.component.html',
  standalone: false,
})
export class LoginUnauthenticatedComponent implements OnInit, AfterContentInit, OnChanges {
  @Input() public authInfo: AuthInfo;
  @Input() public forcedEmail: string;
  public email = '';
  public password = '';
  public showErrors: boolean;
  public errorDescription: string;
  public loginForm = new FormGroup({
    password: new FormControl(this.password, [Validators.required]),
    email: new FormControl(this.email, [Validators.required, EmailValidators.emailFormatValidator()]),
  });
  @ViewChild('email') public emailField: ElementRef;
  @ViewChild('password') public passwordField: ElementRef;
  public localStorageEmail: string;
  public loginMessage: string;
  public verifyEmailError = false;
  public enableSelfServiceOnboarding: boolean;
  public capsOn: boolean;
  private _destroy = inject(DestroyRef);

  constructor(
    private _appService: AppService,
    @Inject(GENERIC_AUTH_SERVICE_TOKEN) private _authService: GenericAuthService,
  ) {}

  public ngOnInit(): void {
    this._appService.fetchConfigList().subscribe((config) => {
      const configDict = arrayToDict(config, (a) => a.config_key);
      this.enableSelfServiceOnboarding = configDict.self_service_onboarding_enabled.config_value === true;
    });

    if (this.forcedEmail) {
      this.email = this.forcedEmail;
      this.loginForm.setValue({ password: '', email: this.forcedEmail });
    } else {
      this.localStorageEmail = localStorage.getItem('user_email');
      this.email = this.localStorageEmail;
      this.loginForm.setValue({ password: '', email: this.localStorageEmail });
    }
    this.loginMessage = localStorage.getItem('login_message');
    // !important localStorage is stupid. it might return a string as undefined
    if (this.loginMessage && this.loginMessage === 'undefined') {
      this.loginMessage = null;
    }
    localStorage.removeItem('login_message');

    // RESET ERROR WHEN TYPING
    this.loginForm.controls.password.valueChanges.pipe(takeUntilDestroyed(this._destroy)).subscribe(() => {
      if (this.errorDescription) {
        this.errorDescription = null;
      }
    });
    this.loginForm.controls.email.valueChanges.pipe(takeUntilDestroyed(this._destroy)).subscribe(() => {
      if (this.errorDescription) {
        this.errorDescription = null;
      }
    });
  }

  public ngOnChanges(): void {
    // Some errors are only visible after a redirect, and will be saved on authInfo message.
    // See checkSession in auth.service
    if (this.authInfo.status === AuthStatus.Error) {
      this.localStorageEmail = localStorage.getItem('user_email');
      this.verifyEmailError = this.authInfo.message === 'VERIFY_EMAIL'; // TODO: Switch over to this.authInfo.status
      this.errorDescription = this.authInfo.message;
    }
  }

  public ngAfterContentInit(): void {
    if (this.localStorageEmail) {
      setTimeout(() => {
        this.focusPassword();
      }, 1);
    } else {
      setTimeout(() => {
        this.focusEmail();
      }, 1);
    }
  }

  public onLogin(): void {
    if (this.loginForm.valid) {
      this._authService.login(this.loginForm.value.email, this.loginForm.value.password, (err: Auth0Error) =>
        this.errorCallback(err),
      );
    } else {
      this.showErrors = true;
    }
  }

  public errorCallback(err: Auth0Error | null): void {
    this.verifyEmailError = false;
    if (err) {
      switch (err.code) {
        case 'request_error':
          this.errorDescription = extractTranslation('login.login_form.request_error');
          break;
        case 'timeout':
          this.errorDescription = extractTranslation('login.login_form.timeout');
          break;
        case 'invalid_token':
          this.errorDescription = extractTranslation('login.login_form.invalid_token');
          break;
        case 'access_denied':
          /**
           * Auth0 returns the "Wrong email or password" error for security reasons, including when:
            - The email has not been verified.
            - The account is blocked.
            The user entered incorrect credentials.
            This behavior is intentional to avoid leaking information about user accounts, as part of credential stuffing protection.
           https://auth0.com/docs/secure/attack-protection/credential-stuffing-protection
            */
          this.errorDescription = extractTranslation('login.login_form.access_denied');
          // Login failed. Please check your email and password. If you've recently signed up, make sure to verify your email address using the link we sent.
          break;
        default:
          // For other error types, use the error description from Auth0
          this.errorDescription = err.description;
      }
    }
  }

  public resetError(): void {
    this.errorDescription = null;
  }

  public redirectLogin(connection: string): void {
    this._authService.authorize(connection);
  }

  public focusEmail(): void {
    this.emailField.nativeElement.focus();
  }
  public focusPassword(): void {
    this.passwordField.nativeElement.focus();
  }
}
